import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Flame } from 'lucide-react';
import { SPRING_CONFIG } from '../../utils/constants';

// Achievement Streaks Component
const StreakCards = ({ streaks }) => {
  const streakTypes = [
    { key: 'gm', icon: Sun, color: '#FFB800', label: 'GM Streak', description: 'Morning greetings' },
    { key: 'gn', icon: Moon, color: '#8B5CF6', label: 'GN Streak', description: 'Night farewells' },
    { key: 'sendit', icon: Flame, color: '#FF4747', label: 'SendIt Streak', description: 'Daily activity' }
  ];
  return (
    <div>
      <h3 className="text-2xl font-bold text-[#E8FDF2] mb-8">Achievement Streaks</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {streakTypes.map((streakType) => {
          const streak = streaks?.[streakType.key] || { current: 0, total: 0, milestones: [] };
          const IconComponent = streakType.icon;
          const nextMilestone = streak.milestones?.find(m => m > streak.current) || 100;
          const progress = (streak.current / nextMilestone) * 100;
          
          return (
            <motion.div 
              key={streakType.key} 
              className="relative p-8 bg-gradient-to-br from-[#0A0E1B]/80 to-[#0D1220]/60 rounded-3xl border border-white/[0.08] hover:border-white/[0.15] transition-all duration-500 overflow-hidden group"
              whileHover={{ scale: 1.02, y: -4 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Background Glow */}
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                style={{ backgroundColor: streakType.color }}
              />
              
              <div className="relative z-10">
                <div className="mb-6" style={{ color: streakType.color }}>
                  <IconComponent className="w-16 h-16 mx-auto" />
                </div>
                
                <div className="text-center mb-6">
                  <motion.div 
                    className="text-5xl font-bold text-[#E8FDF2] mb-1"
                    key={streak.current}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={SPRING_CONFIG}
                  >
                    {streak.current}
                  </motion.div>
                  <div className="text-lg font-medium text-[#E8FDF2]">{streakType.label}</div>
                  <div className="text-sm text-[#8AA5A2] mt-1">{streakType.description}</div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8AA5A2]">Current / Total</span>
                    <span className="text-[#E8FDF2] font-medium">{streak.current} / {streak.total}</span>
                  </div>
                  
                  <div className="h-3 bg-[#0A0E1B]/60 rounded-full overflow-hidden border border-white/10">
                    <motion.div
                      className="h-full relative"
                      style={{ 
                        background: `linear-gradient(90deg, ${streakType.color}80, ${streakType.color})`
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                    >
                      <motion.div
                        className="absolute right-0 top-0 bottom-0 w-1 bg-white/80 rounded-full"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    </motion.div>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8AA5A2]">Next milestone</span>
                    <span className="text-[#E8FDF2] font-medium">{nextMilestone} days</span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default StreakCards;